import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["status", "button", "form"]
  static values = { facilitatorId: String }

  connect() {
    console.log("Contract generation controller connected")
    
    // If we're already showing generating status, start polling
    if (this.hasStatusTarget && this.statusTarget.textContent.includes('Generating Contract')) {
      this.startPolling()
    }
  }

  disconnect() {
    this.stopPolling()
  }

  async generate(event) {
    event.preventDefault()
    
    // Show confirmation dialog
    if (!confirm("This will generate a new contract for this facilitator. Continue?")) {
      return false
    }
    
    // Show generating status immediately
    if (this.hasStatusTarget) {
      this.statusTarget.innerHTML = this.generatingStatusHTML()
    }
    
    // Hide the button and helper text
    if (this.hasButtonTarget) {
      this.buttonTarget.closest('form').style.display = 'none'
    }
    
    const helperText = this.element.querySelector('.text-gray-500')
    if (helperText) {
      helperText.style.display = 'none'
    }
    
    // Submit the form via fetch to avoid page refresh
    const form = this.formTarget
    const url = form.action
    const method = form.method
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content
    
    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new FormData(form)
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      // The Turbo Stream response will handle updating the UI
      const text = await response.text()
      if (text.includes('turbo-stream')) {
        // Let Turbo handle the stream
        Turbo.renderStreamMessage(text)
      }
      
      // Start polling to check for contract creation
      this.startPolling()
    } catch (error) {
      console.error('Error generating contract:', error)
      this.statusTarget.innerHTML = this.errorStatusHTML(error.message)
    }
    
    return false
  }

  startPolling() {
    console.log('Starting to poll for contract status...')
    this.pollingInterval = setInterval(() => {
      this.checkContractStatus()
    }, 3000) // Check every 3 seconds
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
  }

  async checkContractStatus() {
    try {
      // Fetch the current page to check if contract exists
      const response = await fetch(window.location.href, {
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      const html = await response.text()
      
      // Check if the contract status section exists in the response
      if (html.includes('Contract Status') && html.includes('Contract ID')) {
        console.log('Contract found! Refreshing page...')
        this.stopPolling()
        window.location.reload()
      }
    } catch (error) {
      console.error('Error checking contract status:', error)
    }
  }

  generatingStatusHTML() {
    return `
      <div class="rounded-md bg-blue-50 p-4 mb-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="animate-spin h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-blue-800">Generating Contract...</h3>
            <div class="mt-2 text-sm text-blue-700">
              <p>Please wait while the contract is being generated. This page will update automatically.</p>
            </div>
          </div>
        </div>
      </div>
    `
  }

  errorStatusHTML(message) {
    return `
      <div class="rounded-md bg-red-50 p-4 mb-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Contract Generation Failed</h3>
            <div class="mt-2 text-sm text-red-700">
              <p>Error: ${message}</p>
            </div>
          </div>
        </div>
      </div>
    `
  }
}